import {
  Box,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  useToast,
  Text,
  Button,
  Flex,
} from "@chakra-ui/react";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "@/contexts/proposalFormContext";
import { getProposalFormSteps } from "./getProposalFormSteps";
import { steps } from "../ProposaFormStepper";
import { useNavigate, useParams } from "react-router-dom";
import {
  getProposalByBalanceSolicitationId,
  updateProposal,
} from "@/services/proposal.service";
import { mergeFormDataWithProposal } from "./mergeFormDataWithProposal";
import Logo from "../Logo";
import "./styles.css";
import { useProposalStore } from "@/contexts/useProposalStore";
import { useUserInfo } from "@/contexts/userInfoContext";
import { formatPhone } from "@/utils/formatPhone";
import { FaWhatsapp } from "react-icons/fa";
import { useSlug } from "@/contexts/selfServiceSlugContext";

export default function ProposalForm() {
  const [activeStep, setActiveStep] = useState<number>(0);
  const { formData, updateFormData } = useForm();
  const { user } = useUserInfo();
  const { selfServiceSlug } = useSlug();
  const { slug, slugSeller, slugSelfService } = useParams<{
    slug?: string;
    slugSeller?: string;
    slugSelfService?: string;
  }>();
  const toast = useToast();
  const navigate = useNavigate();
  const { id } = useParams();
  const { setProposal } = useProposalStore();
  const whatsappNumber = selfServiceSlug?.supportPhone;
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingConfirmation, setLoadingConfirmation] =
    useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const loadProposal = useCallback(
    async (proposalId: string) => {
      setLoading(false);
      try {
        const proposal = await getProposalByBalanceSolicitationId(proposalId);
        if (proposal) {
          updateFormData("proposal", proposal);
          if (
            proposal.optionsFinancialInfo &&
            proposal.optionsFinancialInfo.length > 0
          ) {
            updateFormData("resultStep", {
              chosenFinancialOption: proposal.optionsFinancialInfo.find(
                (option) => option.productId === selfServiceSlug?.productId
              ),
            });
          }
        } else {
          throw new Error("No proposal data available.");
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to fetch the proposal.";
        setError(errorMessage);
        toast({
          title: "Erro ao carregar proposta",
          description: errorMessage,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    },
    [toast, updateFormData]
  );

  useEffect(() => {
    if (id && loading) {
      loadProposal(id);
    }
  }, [id, loadProposal, loading]);

  useEffect(() => {
    updateFormData("personalData", {
      name: user.name,
      cellphone: formatPhone(user.phone),
    });
  }, []);

  const handleNext = useCallback(() => {
    if (activeStep < steps.length - 1) {
      setActiveStep((prev) => prev + 1);
    } else {
      toast({
        title: "Proposta concluída!",
        description: "Sua proposta foi enviada com sucesso.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    }
  }, [activeStep, toast]);

  const handleBack = useCallback(() => {
    if (activeStep === 0) {
      navigate(
        slugSelfService
          ? `/${slug}/${slugSeller}/${slugSelfService}/consulta-cpf`
          : slugSeller
          ? `/${slug}/${slugSeller}/consulta-cpf`
          : `/${slug}/consulta-cpf`
      );
    } else {
      setActiveStep((prev) => prev - 1);
    }
  }, [activeStep, navigate]);

  const removeMask = (value?: string) => {
    if (!value) return value;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (emailRegex.test(value)) {
      return value;
    }
    return value.replace(/\D/g, "");
  };

  const handleSubmit = useCallback(async () => {
    try {
      if (!formData.proposal) {
        toast({
          title: "Erro na operação",
          description: "Proposta não encontrada.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        handleBack();
        return;
      }
      setLoadingConfirmation(true);
      formData.personalData.cellphone = removeMask(
        formData.personalData.cellphone
      );
      formData.address.cep = removeMask(formData.address.cep);
      formData.bankData.pixKey = removeMask(formData.bankData.pixKey);

      const newProposal = mergeFormDataWithProposal(formData);

      const updatedProposal = await updateProposal(newProposal).finally(() => {
        setLoadingConfirmation(false);
      });
      setProposal(updatedProposal);

      toast({
        title: `Proposta de ${newProposal.NaturalPerson?.name} feita!`,
        description: "Todas as informações foram confirmadas com sucesso.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      navigate(`/${slug}/proposal/link`, {
        state: { proposal: updatedProposal },
      });
    } catch (error) {
      console.error(error);
      toast({
        title: "Erro na operação",
        description: "Ocorreu um erro ao processar suas informações.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      handleBack();
    }
  }, [formData, updateFormData, toast]);

  const handleAdvance = () => {
    if (activeStep === steps.length - 1) {
      handleSubmit();
    } else {
      handleNext();
    }
  };

  const stepsData = getProposalFormSteps(
    formData,
    updateFormData,
    handleAdvance,
    handleBack,
    handleSubmit
  );
  const currentStep = stepsData[activeStep];

  if (error) return <Box>{error}</Box>;

  const handleSupport = () => {
    let phoneNumber = "+55" + whatsappNumber?.replace(/\D/g, "");

    const message =
      "Olá, estou tentando antecipar meu saque aniversário e tenho uma dúvida!";
    const whatsappURL = `https://wa.me/${phoneNumber}/?text=${encodeURIComponent(
      message
    )}`;

    window.open(whatsappURL, "_blank");
  };

  return (
    <Box width="100%" bg={"gray.100"} minHeight={"100vh"} p={6}>
      <Modal isOpen={loadingConfirmation} onClose={() => {}} isCentered>
        <ModalOverlay />
        <ModalContent bg="transparent" boxShadow="none">
          <ModalBody display="flex" flexDirection="column" alignItems="center">
            <Logo
              variant="primary"
              w="100px"
              alt="Logo"
              animation={`pulse 2s infinite`}
            />
            <Text mt={4} fontSize="xl" color="white" fontWeight="bold">
              Estamos enviando a sua proposta.
            </Text>
          </ModalBody>
        </ModalContent>
      </Modal>

      <Flex justifyContent={"flex-end"} w={"100%"}>
        <Button
          onClick={handleSupport}
          size="sm"
          variant="outline"
          leftIcon={<FaWhatsapp size={16} />}
          color="primary"
          bg="white"
          _hover={{ bg: "white", color: "primary" }}
        >
          Suporte
        </Button>
      </Flex>

      {formData.resultStep.chosenFinancialOption && (
        <>{currentStep.component}</>
      )}
    </Box>
  );
}
